import { useCallback, useContext } from "react";
import { YStack } from "tamagui";

import { useSendNoriMessage } from "@/api/mutations/conversation";
import { ConstrainedView } from "@/components";
import { LayoutContext } from "@/components/LayoutProvider";
import { useCurrentConversationId } from "@/hooks/conversation";
import {
  type AskUserAnswer,
  buildQuestionAnswer,
  collectAttachments,
  useAskUserQuestionsStore,
} from "@/store/askUserQuestions";
import { getShadow } from "@/theme/helpers";
import PanelHeader from "./PanelHeader";
import QuestionBody from "./QuestionBody";

export const useAskUserQuestionContainerStyles = () => {
  const { chatScrollViewHeight } = useContext(LayoutContext);

  return {
    paddingTop: "$250",
    paddingBottom: "$100",
    paddingHorizontal: "$100",
    maxHeight: chatScrollViewHeight ? chatScrollViewHeight * 0.85 : 600,
    backgroundColor: "$fillModal",
    borderRadius: "$150",
    borderWidth: "$strokeWidth",
    borderColor: "$strokeNeutral",
    overflow: "hidden",
    ...getShadow({ y: 10, blur: "40px" }),
  } as const;
};

export default function AskUserQuestionsPanel() {
  const conversationId = useCurrentConversationId();
  const { sendNoriMessage } = useSendNoriMessage(conversationId);
  const {
    session,
    setAnswer,
    nextQuestion,
    prevQuestion,
    skipQuestion,
    clearSession,
  } = useAskUserQuestionsStore();

  const currentIndex = session?.currentIndex ?? 0;
  const onChange = useCallback(
    (answer: AskUserAnswer) => setAnswer(currentIndex, answer),
    [setAnswer, currentIndex],
  );

  const submitAll = useCallback(() => {
    const current = useAskUserQuestionsStore.getState().session;
    if (!current) return;
    const questionAnswers = current.questions.map((q, i) =>
      buildQuestionAnswer(q, current.answers[i]),
    );
    const { attachmentIds, attachments } = collectAttachments(
      current.questions,
      current.answers,
      current.attachments,
    );
    clearSession();
    sendNoriMessage(questionAnswers, {
      attachmentIds,
      attachments,
      type: "ask_user_questions_response",
    });
  }, [clearSession, sendNoriMessage]);

  const containerStyles = useAskUserQuestionContainerStyles();

  if (!session) return null;

  const question = session.questions[currentIndex];
  if (!question) return null;

  const isLast = currentIndex === session.questions.length - 1;
  const answer = session.answers[currentIndex];

  const onSubmit = () => (isLast ? submitAll() : nextQuestion());
  const onSkip = () => {
    skipQuestion(currentIndex);
    if (isLast) submitAll();
  };

  return (
    <ConstrainedView>
      <YStack {...containerStyles}>
        <PanelHeader
          question={question.question}
          index={currentIndex}
          total={session.questions.length}
          onPrev={prevQuestion}
          onNext={nextQuestion}
        />
        <QuestionBody
          question={question}
          questionIndex={currentIndex}
          answer={answer}
          onChange={onChange}
          onSubmit={onSubmit}
          onSkip={onSkip}
          isLast={isLast}
        />
      </YStack>
    </ConstrainedView>
  );
}
